import type { PendingCoComment } from "@/features/co-review/api/coReviewApi";
import type { PendingNarrativeComment } from "../../../components/coReview/NarrativeWithInlineComments";

/** The JSON-path segment narrative comments anchor to — part of the staged
 *  payload contract shared with the server (see coReviewCommentAdapter). */
export const NARRATIVES_KEY = "Narratives";

export const newPendingId = () =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `p_${Math.random().toString(36).slice(2)}_${Date.now()}`;

export interface PendingSectionRejection {
  pendingId: string;
  sectionKey: string;
  sectionLabel: string;
  sectionPath: string;
  commentText: string;
}

export interface PendingGeneralComment {
  pendingId: string;
  commentText: string;
}

export type StagedNarrativeComment = PendingNarrativeComment & {
  narrativeIndex: number;
};

/**
 * Everything the CO stages locally before "Request changes" submits it in
 * one batch: per-section rejections (spec §1, one per section — restaging
 * replaces), inline narrative comments (spec §2), free-form general
 * comments, and the sidebar composer draft. One reducer because the one
 * cross-field transition (clearAll after a successful reject) and the
 * staged-count gating read all four together.
 */
export interface StagedFeedbackState {
  sectionRejections: PendingSectionRejection[];
  narrativeComments: StagedNarrativeComment[];
  generalComments: PendingGeneralComment[];
  generalDraft: string;
}

export const initialStagedFeedback: StagedFeedbackState = {
  sectionRejections: [],
  narrativeComments: [],
  generalComments: [],
  generalDraft: "",
};

export type StagedFeedbackAction =
  | {
      type: "stageSectionRejection";
      section: { key: string; label: string; sectionPath: string };
      text: string;
    }
  | { type: "editSectionRejection"; pendingId: string; text: string }
  | { type: "clearSectionRejection"; pendingId: string }
  | {
      type: "stageNarrativeComment";
      narrativeIndex: number;
      charStart: number;
      charEnd: number;
      quotedText: string;
      commentText: string;
    }
  | { type: "deleteNarrativeComment"; pendingId: string }
  | { type: "addGeneralComment"; text: string }
  | { type: "editGeneralComment"; pendingId: string; text: string }
  | { type: "deleteGeneralComment"; pendingId: string }
  | { type: "setGeneralDraft"; text: string }
  | { type: "clearAll" };

export function stagedFeedbackReducer(
  state: StagedFeedbackState,
  action: StagedFeedbackAction,
): StagedFeedbackState {
  switch (action.type) {
    case "stageSectionRejection":
      // One rejection per section: restaging replaces the previous one.
      return {
        ...state,
        sectionRejections: [
          ...state.sectionRejections.filter(
            (p) => p.sectionKey !== action.section.key,
          ),
          {
            pendingId: newPendingId(),
            sectionKey: action.section.key,
            sectionLabel: action.section.label,
            sectionPath: action.section.sectionPath,
            commentText: action.text,
          },
        ],
      };
    case "editSectionRejection":
      return {
        ...state,
        sectionRejections: state.sectionRejections.map((p) =>
          p.pendingId === action.pendingId
            ? { ...p, commentText: action.text }
            : p,
        ),
      };
    case "clearSectionRejection":
      return {
        ...state,
        sectionRejections: state.sectionRejections.filter(
          (p) => p.pendingId !== action.pendingId,
        ),
      };
    case "stageNarrativeComment":
      return {
        ...state,
        narrativeComments: [
          ...state.narrativeComments,
          {
            pendingId: newPendingId(),
            narrativeIndex: action.narrativeIndex,
            charStart: action.charStart,
            charEnd: action.charEnd,
            quotedText: action.quotedText,
            commentText: action.commentText,
          },
        ],
      };
    case "deleteNarrativeComment":
      return {
        ...state,
        narrativeComments: state.narrativeComments.filter(
          (p) => p.pendingId !== action.pendingId,
        ),
      };
    case "addGeneralComment": {
      const trimmed = action.text.trim();
      if (!trimmed) return state;
      return {
        ...state,
        generalComments: [
          ...state.generalComments,
          { pendingId: newPendingId(), commentText: trimmed },
        ],
      };
    }
    case "editGeneralComment":
      return {
        ...state,
        generalComments: state.generalComments.map((p) =>
          p.pendingId === action.pendingId
            ? { ...p, commentText: action.text }
            : p,
        ),
      };
    case "deleteGeneralComment":
      return {
        ...state,
        generalComments: state.generalComments.filter(
          (p) => p.pendingId !== action.pendingId,
        ),
      };
    case "setGeneralDraft":
      return { ...state, generalDraft: action.text };
    case "clearAll":
      return initialStagedFeedback;
    default:
      return state;
  }
}

/**
 * The "Request changes" payload, extracted verbatim from CoReviewPage's
 * handleRejectReport. Every staged comment is tagged with the CO's display
 * name so officers (who can't read the user directory) still see who wrote
 * each comment.
 */
export function toPendingCoComments(
  state: StagedFeedbackState,
  authorName: string,
): PendingCoComment[] {
  return [
    ...state.sectionRejections.map((r) => ({
      sectionPath: r.sectionPath,
      fieldLabel: r.sectionLabel,
      commentText: r.commentText,
      isSectionRejection: true,
      authorName,
    })),
    ...state.narrativeComments
      .filter((p) => typeof p.narrativeIndex === "number")
      .map((p) => ({
        sectionPath: `$.${NARRATIVES_KEY}[${p.narrativeIndex}]`,
        fieldLabel: `Narrative ${p.narrativeIndex + 1}`,
        commentText: p.commentText,
        narrativeIndex: p.narrativeIndex,
        charStart: p.charStart,
        charEnd: p.charEnd,
        quotedText: p.quotedText,
        authorName,
      })),
    ...state.generalComments.map((g) => ({
      sectionPath: "$",
      fieldLabel: "General",
      commentText: g.commentText,
      authorName,
    })),
  ];
}
